import React, { useState, useEffect } from 'react';
import { Shield, Check, X as XIcon, Sparkles, Lock, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * One-time modal shown after a user logs in and the server reports they
 * haven't accepted the current published policy version.
 *
 * Non-dismissable in the traditional sense (no X in the corner) — the user
 * must either Accept or Sign Out. This mirrors industry standard TOS refresh flows.
 */
const PolicyAcceptanceModal = ({ onAccepted, onSignOut }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkedRead, setCheckedRead] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('user_token');
    if (!token) return;
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/users/policies/status`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        if (cancelled) return;
        setStatus(r.data);
        if (r.data.needs_acceptance) setVisible(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const accept = async () => {
    if (!status || !checkedRead) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('user_token');
      await axios.post(
        `${BACKEND_URL}/api/users/policies/accept`,
        { version: status.current_version },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Thanks — your policy acknowledgement is on file.');
      setVisible(false);
      onAccepted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save acknowledgement');
    } finally {
      setLoading(false);
    }
  };

  const signOut = () => {
    localStorage.removeItem('user_token');
    localStorage.removeItem('user_data');
    onSignOut?.();
    window.location.href = '/';
  };

  if (!visible || !status) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center px-4 py-8"
      data-testid="policy-acceptance-modal"
      style={{ animation: 'polModalFade 0.3s ease-out' }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />

      {/* Panel */}
      <div
        className="relative z-10 w-full max-w-lg bg-[#0A0A14] border border-white/[0.08] rounded-3xl overflow-hidden shadow-2xl"
        style={{ animation: 'polModalIn 0.35s cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        {/* Header */}
        <div className="relative px-7 pt-7 pb-5 border-b border-white/[0.06]">
          <div className="absolute inset-0 bg-gradient-to-br from-[#00E5FF]/[0.06] via-transparent to-[#8A2BE2]/[0.05] pointer-events-none" />
          <div className="relative flex items-start gap-4">
            <div className="w-11 h-11 rounded-2xl bg-[#00E5FF]/15 border border-[#00E5FF]/30 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-[#00E5FF]" />
            </div>
            <div className="flex-1">
              <h2 className="text-white text-lg font-medium">We've refreshed our policies</h2>
              <p className="text-white/50 text-xs mt-1 leading-relaxed">
                Before you continue, please take a moment to review the highlights and confirm you're on board with the latest version.
              </p>
            </div>
          </div>
        </div>

        {/* Highlights */}
        <div className="px-7 py-5 space-y-2.5 max-h-[46vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-1">
            <p className="text-white/40 text-[11px] uppercase tracking-wider font-medium">What's new</p>
            <span className="text-white/25 text-[10px] font-mono">v{status.current_version}</span>
          </div>
          {(status.highlights || []).map((h, i) => (
            <div key={i} className="flex items-start gap-3 p-3.5 rounded-xl bg-white/[0.02] border border-white/[0.05]">
              <div className="w-7 h-7 rounded-lg bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center flex-shrink-0">
                <Check className="w-3.5 h-3.5 text-emerald-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium">{h.title}</p>
                <p className="text-white/50 text-xs mt-0.5 leading-relaxed">{h.body}</p>
              </div>
            </div>
          ))}

          <a
            href="/account/settings?tab=privacy"
            target="_blank"
            rel="noreferrer"
            data-testid="policy-modal-read-full-link"
            className="mt-2 inline-flex items-center gap-1.5 text-[#00E5FF] hover:text-white text-xs font-medium transition-colors"
          >
            Read the full policies in Data &amp; Privacy
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {/* Checkbox */}
        <div className="px-7 pb-5">
          <label className="flex items-start gap-3 p-3.5 rounded-xl bg-white/[0.02] border border-white/[0.08] cursor-pointer hover:bg-white/[0.04] transition-all">
            <input
              type="checkbox"
              checked={checkedRead}
              onChange={(e) => setCheckedRead(e.target.checked)}
              data-testid="policy-modal-checkbox"
              className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/[0.03] accent-[#00E5FF] cursor-pointer"
            />
            <span className="text-white/70 text-xs leading-relaxed">
              I have read and agree to the refreshed{' '}
              <span className="text-white font-medium">Privacy Policy, Terms of Service, Cookie Policy, Community Guidelines,</span>{' '}
              and related policies.
            </span>
          </label>
        </div>

        {/* Actions */}
        <div className="px-7 pb-7 flex items-center gap-3">
          <button
            onClick={signOut}
            data-testid="policy-modal-signout-btn"
            className="px-5 py-2.5 rounded-xl text-white/50 hover:text-white/80 text-sm transition-colors"
          >
            Sign out
          </button>
          <Button
            onClick={accept}
            disabled={!checkedRead || loading}
            data-testid="policy-modal-accept-btn"
            className="flex-1 bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium py-2.5 rounded-xl text-sm transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving…' : 'Agree & continue'}
            {!loading && <Check className="w-4 h-4 ml-2" />}
          </Button>
        </div>

        {/* Footer trust */}
        <div className="px-7 py-3 bg-white/[0.02] border-t border-white/[0.05] flex items-center gap-2 text-white/30 text-[10px]">
          <Lock className="w-3 h-3" />
          <span>Your acknowledgement is logged for compliance. You can review any policy at any time.</span>
        </div>
      </div>

      <style>{`
        @keyframes polModalFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes polModalIn {
          from { opacity: 0; transform: translateY(20px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default PolicyAcceptanceModal;
